/* ONE TUNNE <order-tracking>: the order tracking widget.
 *
 * The element talks to ONE endpoint the merchant configured (data-endpoint) — by design the
 * path of a Shopify App Proxy (same origin, e.g. /apps/track-order), so the theme itself never
 * calls a third-party domain and no carrier API secret ships to the browser. A full URL also
 * works while prototyping. Expected JSON:
 *   { "status": "in_transit", "courier": "…", "eta": "2026-10-10",
 *     "checkpoints": [{ "time": "ISO 8601", "label": "…", "location": "…" }] }
 * 404 means "number not found"; anything else non-OK is a generic failure.
 *
 * Sample mode (data-demo) renders a clearly badged fixture built from the i18n island —
 * no real courier names, no invented facts presented as facts.
 *
 * No framework, no analytics; every string comes from the JSON island Liquid rendered,
 * everything is inserted via textContent, never innerHTML with API data.
 */
(() => {
  if (customElements.get('order-tracking')) return;

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  };

  class OrderTracking extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-trk-form]');
      this.out = this.querySelector('[data-trk-out]');
      this.input = this.querySelector('[data-trk-input]');
      this.button = this.querySelector('[data-trk-submit]');
      this.label = this.querySelector('[data-trk-submit-label]');
      const island = this.querySelector('[data-trk-i18n]');
      try { this.i18n = island ? JSON.parse(island.textContent) || {} : {}; } catch (e) { this.i18n = {}; }
      if (this.form) this.form.addEventListener('submit', this);
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this);
    }

    t(key) { return this.i18n[key] || ''; }
    statusLabel(code) { return (this.i18n.status || {})[code] || code || ''; }

    date(value, dateOnly) {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value || '');
      const lang = document.documentElement.lang || 'en';
      const opts = dateOnly
        ? { day: 'numeric', month: 'short' }
        : { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' };
      try { return new Intl.DateTimeFormat(lang, opts).format(d); } catch (e) { return d.toLocaleString(); }
    }

    busy(on) {
      if (this.button) this.button.disabled = on;
      if (this.label) this.label.textContent = on ? this.t('loading') : this.t('submit');
    }

    async handleEvent(event) {
      event.preventDefault();
      const number = this.input && this.input.value.trim();
      if (!number) { if (this.input) this.input.focus(); return; }

      if (this.hasAttribute('data-demo')) { this.render(this.demoData(), true); return; }

      const endpoint = this.getAttribute('data-endpoint');
      if (!endpoint) { this.fail('error_generic'); return; }

      this.busy(true);
      try {
        const url = new URL(endpoint, window.location.origin);
        url.searchParams.set('number', number);
        url.searchParams.set('lang', (document.documentElement.lang || 'en').slice(0, 2));
        const token = this.getAttribute('data-token');
        if (token) url.searchParams.set('token', token);
        const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
        if (response.status === 404) { this.fail('error_notfound'); return; }
        if (!response.ok) { this.fail('error_generic'); return; }
        this.render(await response.json(), false);
      } catch (e) {
        this.fail('error_generic');
      } finally {
        this.busy(false);
      }
    }

    fail(key) {
      if (!this.out) return;
      this.out.textContent = '';
      const box = el('p', 'trk__error', this.t(key));
      box.setAttribute('role', 'alert');
      this.out.appendChild(box);
    }

    demoData() {
      const day = 86400000;
      const now = Date.now();
      const labels = this.i18n.demo || [];
      return {
        status: 'in_transit',
        eta: new Date(now + 2 * day).toISOString(),
        checkpoints: labels.map((label, i) => ({
          time: new Date(now - [4, 2, 1, 0.2][i] * day || now).toISOString(),
          label: label,
          location: ''
        }))
      };
    }

    render(data, demo) {
      if (!this.out || !data) return;
      this.out.textContent = '';
      const result = el('div', 'trk__result');

      if (demo) result.appendChild(el('p', 'trk__badge mono mono--sm', this.t('demo_badge')));

      // The carrier's own wording (already in the page language) wins over the generic label.
      const code = String(data.status || '');
      const statusText = String(data.statusText || '') || this.statusLabel(code);
      if (statusText) {
        const status = el('p', 'trk__status');
        status.dataset.trkStatus = code;
        status.appendChild(el('span', 'trk__dot'));
        status.appendChild(el('span', 'trk__status-name', statusText));
        result.appendChild(status);
      }

      const meta = [];
      if (data.courier) meta.push(this.t('courier') + ': ' + data.courier);
      if (data.eta) meta.push(this.t('eta') + ': ' + this.date(data.eta, true));
      if (meta.length) result.appendChild(el('p', 'trk__meta', meta.join(' · ')));

      const points = Array.isArray(data.checkpoints) ? data.checkpoints.slice() : [];
      if (points.length) {
        points.sort((a, b) => new Date(b.time) - new Date(a.time));
        const list = el('ol', 'trk__timeline');
        for (const point of points) {
          const item = el('li', 'trk__point');
          item.appendChild(el('span', 'trk__time mono mono--sm', this.date(point.time)));
          const body = el('span', 'trk__point-body');
          body.appendChild(el('span', 'trk__point-label', String(point.label || '')));
          if (point.location) body.appendChild(el('span', 'trk__point-loc', String(point.location)));
          item.appendChild(body);
          list.appendChild(item);
        }
        result.appendChild(list);
      } else {
        result.appendChild(el('p', 'trk__meta', this.t('empty_checkpoints')));
      }

      this.out.appendChild(result);
    }
  }

  customElements.define('order-tracking', OrderTracking);
})();
